'use client';

import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { calculateContextMenuPosition, openContextMenuEvent } from '@nx-frontend/shared/ui/context-menu';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { openModalEvent, closeModalEvent } from '@nx-frontend/shared/ui/modal/modal-store';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { postPortalContentClientApi } from '@nx-frontend/shared/api';
import { clientExportNote } from '@nx-frontend/shared/api/client/client-export-note';
import { getPortalContents } from '@nx-frontend/shared/api/webnotes/client';
import { useRouter, useParams } from '@nx-frontend/shared/hooks/router-proxy';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $menuState, deleteMenuItemEvent } from '@nx-frontend/shared/store/feature-menu-tree';
import { addJobEvent } from '@nx-frontend/shared/store/jobs';
import { $note, updateCurrentNoteEvent } from '@nx-frontend/shared/store/note';
import { setNoteFullScreenEvent } from '@nx-frontend/shared/store/note-fullscreen';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $portalSettings } from '@nx-frontend/shared/store/portal';
import { $user } from '@nx-frontend/shared/store/user';
import { $webEditor } from '@nx-frontend/shared/store/web-editor-store';
import {
  NoteServiceNoteExportParamsDto,
  OrganizationPrivilege,
  PaywallPopupReason,
  PopupType,
} from '@nx-frontend/shared/types';
import {
  MenuItem,
  MenuType,
  MenuItemType,
  PortalServiceChangeEventDto,
  PortalServiceContentDto,
  PortalServiceMenuItemDtoPositionType,
} from '@nx-frontend/shared/types';
import { UiContextMenuConfigPosition, UiContextMenuTheme } from '@nx-frontend/shared/types';
import { getOpenPaywallPopup } from '@nx-frontend/widgets/popups';
import { getPageMenuItems } from './page-menu-data';

export function PageMenu() {
  const [
    openContextMenu,
    currentNote,
    editor,
    updateNote,
    openModal,
    closeModal,
    addJob,
    menuState,
    deleteMenuItem,
    portalSettings,
  ] = useUnit([
    openContextMenuEvent,
    $note,
    $webEditor,
    updateCurrentNoteEvent,
    openModalEvent,
    closeModalEvent,
    addJobEvent,
    $menuState,
    deleteMenuItemEvent,
    $portalSettings,
  ]);

  const t = useTranslations();

  const router = useRouter();
  const params = useParams();
  const slug = useMemo(() => (params?.slug as string[]) || [], [params?.slug]);
  const routerPush = useCallback((url: string) => router.push(url), [router]);

  const [orgInfo, webEditor, currentOrganization, user] = useUnit([$orgInfo, $webEditor, $currentOrganization, $user]);
  const orgId = orgInfo?.orgId;

  const [searchContainerVisible, setSearchContainerVisible] = useState(false);

  const [setNoteFullScreen] = useUnit([setNoteFullScreenEvent]);

  const { toast } = useToast();

  useEffect(() => {
    if (editor && editor.plugins?.search) {
      editor.plugins.search.options.onSearchStart = () => {
        setSearchContainerVisible(true);
      };
      editor.plugins.search.options.onSearchEnd = () => {
        setSearchContainerVisible(false);
      };
    }
  }, [editor]);

  useEffect(() => {
    if (editor) {
      editor.allowUndoRedoWithoutFocus = true;
    }
  }, [editor]);

  const onSearch = useCallback(() => {
    if (editor && editor.plugins?.search) {
      editor.plugins.search.start();
    }
  }, [editor]);

  const onUndo = useCallback(() => {
    if (editor) {
      editor.exec('undo');
    }
  }, [editor]);

  const onRedo = useCallback(() => {
    if (editor) {
      editor.exec('redo');
    }
  }, [editor]);

  const editorStyle = webEditor?.plugins?.editorStyle;

  const handlePageMenuClick = useCallback(
    (event: MouseEvent) => {
      const menu = getPageMenuItems(
        {
          note: currentNote,
          slug,
          routerPush,
          onSearch,
          onUndo,
          onRedo,
          updateNote,
          openModal,
          closeModal,
          onDownload: async (format: string, size?: string) => {
            if (!orgInfo.subscriptionPrivileges?.includes(OrganizationPrivilege.CAN_EXPORT_PDF) && format === 'pdf') {
              if (currentOrganization.userId === user.id) {
                getOpenPaywallPopup({
                  openModal,
                  payload: {
                    payloadType: PopupType.PAYWALL,
                    mode: 'upgrade',
                    title: t('features.paywall.title_download_pages_in_pdf'),
                    reason: PaywallPopupReason.DOWNLOAD_PDF,
                    orgId,
                  },
                });
              } else {
                openModal({
                  title: t('webnotes.webnotes-page_downloading'),
                  content: PopupType.DIALOG,
                  closeButtonSize: 18,
                  payload: {
                    payloadType: PopupType.DIALOG,
                    text: t('webnotes.webnotes-page_downloading_restricted'),
                    buttons: [
                      {
                        text: t('webnotes.webnotes-ok'),
                        main: true,
                      },
                    ],
                  },
                });
              }
              return;
            }

            const appearance = editorStyle?.getStyle() ?? { style: undefined, size: undefined };

            const exportOptions: NoteServiceNoteExportParamsDto = {
              language: 'en',
              timezone: new Date().getTimezoneOffset(),
              format,
              style: appearance.style,
              size: appearance.size,
              paperFormat: size,
            };

            toast({
              title: t('webnotes.webnotes-download_is_in_progress'),
            });

            try {
              const task = await clientExportNote({
                workspaceId: currentNote.workspaceId,
                noteId: currentNote.globalId,
                options: exportOptions,
              });
              addJob(task.id);
            } catch (err) {
              console.log('cant export note', err);
            }
          },
          onDelete: async () => {
            setNoteFullScreen(false);
            let menuItem: MenuItem | null = null;
            let menuType: MenuType | null = null;
            for (const menu of [MenuType.PORTAL_SIDEBAR, MenuType.PORTAL_FOOTER, MenuType.PORTAL_TOPBAR]) {
              if (Object.keys(menuState[menu].entities).length) {
                for (const id in menuState[menu].entities) {
                  const item = menuState[menu].entities[id];
                  if (item.targetId === currentNote.globalId) {
                    menuItem = item;
                    menuType = menu;
                    break;
                  }
                }
              }
              if (menuItem) {
                break;
              }
            }
            if (menuItem && menuType) {
              if (menuItem.type === MenuItemType.NOTE || menuItem.type === MenuItemType.PORTAL_PAGE) {
                deleteMenuItem({
                  menuType,
                  item: menuItem,
                });
                const oldValue = Object.assign(
                  {
                    positionType: 'sidebar',
                  } as { positionType: PortalServiceMenuItemDtoPositionType },
                  menuItem
                );
                if (!oldValue.positionType) {
                  oldValue.positionType = 'sidebar';
                }

                let portalContent: PortalServiceContentDto | null = null;

                // get portal conent to delete block
                try {
                  portalContent = await getPortalContents({
                    path: menuItem.url,
                    portalDomain: portalSettings.domain,
                  });
                } catch (err) {
                  console.error(`can't get portal contents`, err);
                }

                const changeEventsDto: PortalServiceChangeEventDto[] = [
                  {
                    type: 'deleteMenuItem',
                    oldValue,
                    newValue: undefined,
                    meta: {
                      userId: 0,
                      pageId: portalContent?.page?.id ?? '',
                    },
                  },
                ];

                const block = portalContent?.page?.blocks?.find((block) => {
                  let blockNoteId = '';
                  let blockWorkspaceId = '';
                  if ('noteId' in block) {
                    blockNoteId = block.noteId;
                  }
                  if ('workspaceId' in block) {
                    blockWorkspaceId = block.workspaceId;
                  }
                  if (!menuItem) {
                    return null;
                  }
                  return blockNoteId === menuItem.targetId && blockWorkspaceId === currentNote.workspaceId;
                });

                if (block) {
                  changeEventsDto.push({
                    type: 'deleteBlock',
                    oldValue: block,
                    newValue: undefined,
                    meta: {
                      userId: 0,
                      pageId: portalContent?.page?.id ?? '',
                    },
                  });
                }

                // TODO: refactor this and move to event/effect
                await postPortalContentClientApi({
                  workspaceId: currentNote.workspaceId,
                  portalId: portalSettings.id,
                  changeEventsDto,
                });
              }
            }
          },
        },
        t
      );
      if (menu) {
        openContextMenu({
          items: menu,
          position: calculateContextMenuPosition(event.target, 7),
          config: {
            position: UiContextMenuConfigPosition.BOTTOM_RIGHT,
            menuPadding: 0,
            theme: UiContextMenuTheme.LIGHT,
          },
        });
      }
    },
    [
      currentNote,
      slug,
      routerPush,
      onSearch,
      onUndo,
      onRedo,
      updateNote,
      openModal,
      closeModal,
      orgInfo.subscriptionPrivileges,
      editorStyle,
      toast,
      currentOrganization.userId,
      user.id,
      orgId,
      addJob,
      setNoteFullScreen,
      menuState,
      deleteMenuItem,
      portalSettings.id,
      portalSettings.domain,
      openContextMenu,
      t,
    ]
  );

  return (
    <div className="relative">
      <button
        data-cy="topbar-pageMenu-button"
        className="flex w-[30px] h-[30px] justify-center items-center rounded hover:bg-zinc-50"
        onClick={handlePageMenuClick}
      >
        <Icon icon={IconsMap.MORE_HORIZONTAL} className="w-[16px] h-[16px]" />
      </button>
      <div
        id="web-editor-search-container"
        className={classNames(
          { hidden: !searchContainerVisible },
          'editor-search-container theme-light absolute top-full right-0 min-w-[325px] z-[1]'
        )}
      ></div>
    </div>
  );
}
